import React from 'react';
import { BackLink, DemoFooter, LenticMark } from '../../components/demo/DemoChrome';
import { RecordScenarioRow } from '../../data/demo';
import { useDemo } from '../../contexts/DemoContext';
const CATEGORY_STYLE: Record<RecordScenarioRow['category'], string> = {
  'Handled and correctly resolved': 'border-brand/40 bg-brand-tint text-brand',
  'Clean case, verified proportionately': 'border-rule bg-paper text-muted',
  'Handled and appropriately escalated': 'border-brand/25 bg-white text-brand',
  'Over-checked, or resolved incorrectly': 'border-signal-amber/40 bg-white text-signal-amber',
  Missed: 'border-signal-red/40 bg-white text-signal-red'
};
const ACTOR_STYLE = {
  candidate: 'text-ink',
  assistant: 'text-brand',
  system: 'text-muted'
};
export function DecisionRecord() {
  const {
    bundle
  } = useDemo();
  const {
    recordScenarioSchedule,
    reconstruction,
    observedBehaviour
  } = bundle;
  return <div className="flex min-h-full w-full flex-col bg-paper" data-id="element-634" data-mp="14yyrezwyrkb">
      <div className="flex items-center justify-between px-8 pt-8" data-id="element-635" data-mp="2uijkki6tex8m">
        <LenticMark data-id="element-636" data-mp="2ujuoqj46a709" />
        <BackLink to="/demo/instrument" label="Back to the instrument" data-id="element-637" data-mp="2pjggp12kmx28" />
      </div>

      <main className="mx-auto w-full max-w-[1000px] flex-1 px-8 py-12" data-id="element-638" data-mp="2pljei79ytuhi">
        <div className="rounded-sm border border-rule bg-white px-5 py-3 font-mono-num text-[11px] text-muted" data-id="element-639" data-mp="2kkvrdwouxxsk">
          {bundle.label} · generated from {bundle.sourceId} · gated {bundle.gatedOn} · reviewed by —
        </div>

        <p className="eyebrow mt-10 text-brand" data-id="element-640" data-mp="3davovah148le">Decision record</p>
        <h1 className="mt-3 text-[30px] font-semibold tracking-tight text-ink" data-id="element-641" data-mp="38agcom8hh750">
          Participant LNT-0417
        </h1>
        <p className="mt-2 max-w-2xl text-[14px] leading-relaxed text-muted" data-id="element-642" data-mp="33h50s6ote068">
          {bundle.recordSubtitle}. No score is produced.
        </p>

        <section className="mt-12" data-id="element-643" data-mp="3vzshl9ws2t1i">
          <p className="eyebrow text-muted" data-id="element-644" data-mp="3w0tu7itfznlc">Scenario schedule</p>
          <table className="mt-4 w-full border-collapse text-left" data-id="element-645" data-mp="3r8kcgq2hdtu5">
            <thead data-id="element-646" data-mp="1pba50e691c62">
              <tr className="border-b border-rule font-mono-num text-[10.5px] uppercase tracking-[0.1em] text-muted" data-id="element-647" data-mp="1pb0acda9egu1">
                <th className="py-2.5 pr-4 font-medium" data-id="element-648" data-mp="1u91smvzv2c4g">Slot</th>
                <th className="py-2.5 pr-4 font-medium" data-id="element-649" data-mp="1flqfwqry0m2r">Class</th>
                <th className="py-2.5 pr-4 font-medium" data-id="element-650" data-mp="1fjcqaxg1822l">Row</th>
                <th className="py-2.5 pr-4 font-medium" data-id="element-651" data-mp="1kjic3wzmeucr">What happened</th>
                <th className="py-2.5 font-medium" data-id="element-652" data-mp="16k96fr91jin1">Read as</th>
              </tr>
            </thead>
            <tbody data-id="element-653" data-mp="1amvqv73du1a7">
              {recordScenarioSchedule.map(r => <tr key={r.slotId} className="border-b border-rule/70 align-top" data-id="element-654" data-mp="1ak18dl3xiqjf">
                  <td className="py-4 pr-4 font-mono-num text-[11.5px] text-muted" data-id="element-655" data-mp="wopchae1eu0x">{r.slotId}</td>
                  <td className="py-4 pr-4 font-mono-num text-[10.5px] uppercase tracking-[0.08em] text-brand" data-id="element-656" data-mp="11ns67yokhdtl">
                    {r.klass}
                  </td>
                  <td className="py-4 pr-4 font-mono-num text-[11.5px] text-muted" data-id="element-657" data-mp="2vynmln5ztx6x">{r.row}</td>
                  <td className="max-w-md py-4 pr-4 text-[13px] leading-relaxed text-ink" data-id="element-658" data-mp="kderzyk3nanf">
                    {r.outcome}
                    <span className="mt-1 block font-mono-num text-[10.5px] text-muted" data-id="element-659" data-mp="fetpfshdkevt">
                      sources: {r.sources}
                    </span>
                  </td>
                  <td className="py-4" data-id="element-660" data-mp="paoep6g3t87k">
                    <span className={`inline-block max-w-[150px] rounded-sm border px-2 py-1 text-[11px] font-medium leading-snug ${CATEGORY_STYLE[r.category]}`} data-id="element-661" data-mp="p9lpr42cdfua">
                      {r.category}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </section>

        <section className="mt-14" data-id="element-662" data-mp="3ervtc473gojn">
          <p className="eyebrow text-muted" data-id="element-663" data-mp="3esxifcky3bkd">Reconstruction · 00:14:31</p>
          <p className="mt-2 max-w-2xl text-[13.5px] leading-relaxed text-muted" data-id="element-664" data-mp="39u2r1m8wweon">
            One window, replayed in order. This is the sequence a reviewer reads to see how a
            conclusion was reached.
          </p>
          <ol className="mt-6 border-l border-rule" data-id="element-665" data-mp="25tw04gtniybq">
            {reconstruction.map((e, i) => <li key={i} className="relative grid grid-cols-[92px_minmax(0,1fr)] gap-4 pb-5 pl-6" data-id="element-666" data-mp="2av1vkmep4zoy" data-mpi={i}>
                <span className="absolute left-0 top-1.5 h-1.5 w-1.5 -translate-x-[3.5px] rounded-full bg-rule" data-id="element-667" data-mp="2evlsppz9s3b3" />
                <span className="font-mono-num text-[11.5px] text-muted" data-id="element-668" data-mp="1i7pgexav7u5o">{e.at}</span>
                <span className={`text-[13.5px] leading-relaxed ${ACTOR_STYLE[e.actor]}`} data-id="element-669" data-mp="1n5h8brbifg9v">
                  <span className="mr-2 font-mono-num text-[10.5px] uppercase tracking-wider text-muted/70" data-id="element-670" data-mp="1r5ri7f2faqu0">
                    {e.actor}
                  </span>
                  {e.text}
                </span>
              </li>)}
          </ol>
        </section>

        <section className="mt-14" data-id="element-671" data-mp="13g6uyjz8l7ex">
          <p className="eyebrow text-muted" data-id="element-672" data-mp="13f5lgjxvcszb">Observed behaviour</p>
          <div className="mt-4 grid gap-5 sm:grid-cols-2" data-id="element-673" data-mp="vwee63ypi2q3">
            {observedBehaviour.map(o => <div key={o.heading} className="rounded-sm border border-rule bg-white p-5" data-id="element-674" data-mp="qzfjnkb4rtrd">
                <h2 className="text-[14px] font-semibold text-ink" data-id="element-675" data-mp="mvq0xbh506e9">{o.heading}</h2>
                <p className="mt-2 text-[13px] leading-relaxed text-muted" data-id="element-676" data-mp="hy7p9dbqjd2k">{o.body}</p>
              </div>)}
          </div>
        </section>

        <section className="mt-14" data-id="element-677" data-mp="d0obeeiy92b5">
          <p className="eyebrow text-muted" data-id="element-678" data-mp="884v39svnt2n">Human review</p>
          <div className="mt-4 rounded-sm border border-dashed border-rule bg-white p-8" data-id="element-679" data-mp="86jzrhyqo63q">
            <div className="grid gap-6 sm:grid-cols-3" data-id="element-680" data-mp="391rzuwgjwxb">
              {['Reviewer', 'Date', 'Decision'].map(f => <div key={f} data-id="element-681" data-mp="3varji8uef81v">
                  <p className="font-mono-num text-[10.5px] uppercase tracking-[0.1em] text-muted" data-id="element-682" data-mp="3qe13tmmiaegz">
                    {f}
                  </p>
                  <div className="mt-3 h-px w-full bg-rule" data-id="element-683" data-mp="3lf6oiunqz81m" />
                </div>)}
            </div>
            <div className="mt-8" data-id="element-684" data-mp="3bsvlrepncidy">
              <p className="font-mono-num text-[10.5px] uppercase tracking-[0.1em] text-muted" data-id="element-685" data-mp="3gqf34nhn5ba4">
                Reasoning
              </p>
              <div className="mt-3 space-y-5" data-id="element-686" data-mp="31u65trhb980c">
                <div className="h-px w-full bg-rule" data-id="element-687" data-mp="36rg1vpz7ghx5" />
                <div className="h-px w-full bg-rule" data-id="element-688" data-mp="36ry8ngjhuejb" />
                <div className="h-px w-2/3 bg-rule" data-id="element-689" data-mp="3ugqycv9ivq5h" />
              </div>
            </div>
          </div>
          <p className="mt-8 text-center text-[15px] text-muted" data-id="element-690" data-mp="3b8yzlom6opf">
            The system described. A person decides.
          </p>
        </section>

        <div className="mt-12 flex justify-center" data-id="element-691" data-mp="3kpeeaptyu4a0">
          <button type="button" className="rounded-sm bg-brand px-7 py-3.5 text-[14px] font-semibold text-white transition-colors duration-150 ease-settle hover:bg-brand-dark" data-id="element-692" data-mp="3ppsghr8vyk04">
            Book a walkthrough
          </button>
        </div>
      </main>

      <DemoFooter data-id="element-693" data-mp="26rciq9x9gclc" />
    </div>;
}